import React from "react";
import {
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
  PlayCircleOutline,
  HelpOutline,
  CheckCircle,
  Error,
  RadioButtonUnchecked,
  CloudUpload,
} from "@material-ui/icons";
import { CircularProgress } from "@material-ui/core";

// Scenario status constants
export const SCENARIO_STATUSES = {
  QUEUED: "QUEUED",
  PROCESSING: "PROCESSING",
  STARTED: "STARTED",
  FINISHED: "FINISHED",
  ERROR: "ERROR",
};

// Input/file status constants
export const FILE_STATUSES = {
  READY: "ready",
  ERROR: "error",
  EMPTY: "empty",
  UPLOADED: "uploaded",
};

// Status groups for easy checking
export const RUNNING_STATUSES = [
  SCENARIO_STATUSES.QUEUED,
  SCENARIO_STATUSES.PROCESSING,
  SCENARIO_STATUSES.STARTED,
];

export const COMPLETED_STATUSES = [
  SCENARIO_STATUSES.FINISHED,
  SCENARIO_STATUSES.ERROR,
];

// Scenario status icons with consistent styling
export const getScenarioStatusIcon = (status, size = 24) => {
  const iconProps = { style: { fontSize: size } };

  switch (status) {
    case SCENARIO_STATUSES.QUEUED:
      return (
        <HourglassEmpty
          {...iconProps}
          style={{ ...iconProps.style, color: "#ff9800" }}
        />
      );
    case SCENARIO_STATUSES.PROCESSING:
      return <CircularProgress size={size} style={{ color: "#4caf50" }} />;
    case SCENARIO_STATUSES.STARTED:
      return (
        <PlayCircleOutline
          {...iconProps}
          style={{ ...iconProps.style, color: "#9c27b0" }}
        />
      );
    case SCENARIO_STATUSES.FINISHED:
      return (
        <CheckCircleOutline
          {...iconProps}
          style={{ ...iconProps.style, color: "#4caf50" }}
        />
      );
    case SCENARIO_STATUSES.ERROR:
      return (
        <ErrorOutline
          {...iconProps}
          style={{ ...iconProps.style, color: "#f44336" }}
        />
      );
    default:
      return (
        <HelpOutline
          {...iconProps}
          style={{ ...iconProps.style, color: "#9e9e9e" }}
        />
      );
  }
};

// File status icons with consistent styling and tooltip support
// eslint-disable-next-line no-unused-vars
export const getFileStatusIcon = (status, className = "", _t = null) => {
  const iconProps = { className };

  switch (status) {
    case FILE_STATUSES.READY:
      return <CheckCircle {...iconProps} style={{ color: "#388e3c" }} />;
    case FILE_STATUSES.ERROR:
      return <Error {...iconProps} style={{ color: "#d32f2f" }} />;
    case FILE_STATUSES.EMPTY:
      return (
        <RadioButtonUnchecked {...iconProps} style={{ color: "#616161" }} />
      );
    case FILE_STATUSES.UPLOADED:
      return <CloudUpload {...iconProps} style={{ color: "#1976d2" }} />;
    default:
      return null;
  }
};

// Get status translation for tooltips
export const getFileStatusTranslation = (status, t) => {
  if (!t) return status;

  const statusMap = {
    [FILE_STATUSES.READY]: t("status.ready"),
    [FILE_STATUSES.ERROR]: t("status.error"),
    [FILE_STATUSES.EMPTY]: t("status.empty"),
    [FILE_STATUSES.UPLOADED]: t("status.uploaded"),
  };
  return statusMap[status] || status;
};

// Status color mappings
export const getScenarioStatusColor = (status) => {
  switch (status) {
    case SCENARIO_STATUSES.QUEUED:
      return "#ff9800";
    case SCENARIO_STATUSES.PROCESSING:
    case SCENARIO_STATUSES.STARTED:
      return "#4caf50";
    case SCENARIO_STATUSES.FINISHED:
      return "#4caf50";
    case SCENARIO_STATUSES.ERROR:
      return "#f44336";
    default:
      return "#9e9e9e";
  }
};

// Status text translations (can be used with i18n)
export const getScenarioStatusText = (status, t) => {
  const statusMap = {
    [SCENARIO_STATUSES.QUEUED]: t?.("status.queued") || "Queued",
    [SCENARIO_STATUSES.PROCESSING]: t?.("status.processing") || "Processing",
    [SCENARIO_STATUSES.STARTED]: t?.("status.started") || "Started",
    [SCENARIO_STATUSES.FINISHED]: t?.("status.finished") || "Finished",
    [SCENARIO_STATUSES.ERROR]: t?.("status.error") || "Error",
  };
  return statusMap[status] || status;
};

// Utility functions
export const isScenarioRunning = (status) => RUNNING_STATUSES.includes(status);
export const isScenarioComplete = (status) =>
  COMPLETED_STATUSES.includes(status);

// File status utility functions
export const isFileStatusComplete = (status) => {
  return status === FILE_STATUSES.READY || status === FILE_STATUSES.UPLOADED;
};

export const isFileStatusError = (status) => {
  return status === FILE_STATUSES.ERROR;
};

// CSS class mappings for styling
export const getScenarioStatusClass = (status) => {
  return `status-${status.toLowerCase()}`;
};

export const getFileStatusClass = (status) => {
  return `file-status-${status.toLowerCase()}`;
};
